import React, { useState, useMemo } from "react";
import uniqueId from "lodash.uniqueid";
import { useTranslation } from "react-i18next";
import {
  DraggableProvidedDragHandleProps,
  DropResult,
} from "react-beautiful-dnd";
import { FieldArrayRenderProps, useFormikContext } from "formik";
import {
  Add,
  Delete,
  Drag,
  Edit,
  Info,
  Top,
} from "@hitachivantara/uikit-react-icons";
import {
  HvGrid,
  HvButton,
  HvTypography,
  HvGlobalActions,
  HvTooltip,
  HvEmptyState,
  useScrollTo,
} from "@hitachivantara/uikit-react-core";
import {
  HvTableColumnConfig,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";

import { OverflowTooltip, DragDropList } from "components/common";
import { actionsColumn, textColumn } from "components/common/SimpleTable";
import StepsDialog, {
  SopStepsDialogInitialValues,
} from "components/sop/SopAddEditStepsDialog";
import useStyles from "./styles";

type SopDataRow = Record<string, unknown> & SopStep;

const SopAddEditSteps: React.FC<FieldArrayRenderProps> = ({
  push,
  remove,
  replace,
  move,
}) => {
  const { t } = useTranslation("sopAddEdit");
  const classes = useStyles();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [dialogValues, setDialogValues] = useState<
    SopStepsDialogInitialValues | undefined
  >();
  const {
    values: { steps },
  } = useFormikContext<SopFormData>();

  const openDialog = (initialValues?: SopStepsDialogInitialValues) => {
    setMode(initialValues ? "edit" : "add");
    setDialogValues(initialValues);
    setIsDialogOpen(true);
  };

  const columns = useMemo(
    (): HvTableColumnConfig<SopDataRow>[] => [
      {
        Header: t("steps.columns.id") as string,
        accessor: "order",
        style: { width: "10%" },
        Cell: ({ row: { index } }: HvCellProps<SopDataRow, string>) => (
          <OverflowTooltip
            data={t("steps.columns.idText", { order: index + 1 }) as string}
          />
        ),
        sortType: "alphanumeric",
      },
      textColumn({
        Header: t("steps.columns.name") as string,
        accessor: "name",
        style: { width: "20%" },
      }),
      textColumn({
        Header: t("steps.columns.description") as string,
        accessor: "description",
        style: { width: "30%" },
      }),
      textColumn({
        Header: t("steps.columns.instructions") as string,
        accessor: "instructions",
        style: { width: "40%" },
      }),
      actionsColumn({
        id: "actions",
        Cell: ({
          row,
          dragHandleProps,
        }: HvCellProps<SopDataRow> & {
          dragHandleProps: DraggableProvidedDragHandleProps;
        }) => {
          return (
            <div className={classes.actionButtons}>
              <HvTooltip
                title={
                  <HvTypography>{t("steps.columns.actions.edit")}</HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("steps.columns.actions.drag")}
                  onClick={() =>
                    openDialog({
                      id: row.original.id as string,
                      name: row.original.name as string,
                      description: row.original.description as string,
                      instructions: row.original.instructions as string,
                    })
                  }
                >
                  <Edit />
                </HvButton>
              </HvTooltip>
              <HvTooltip
                title={
                  <HvTypography>{t("steps.columns.actions.drag")}</HvTypography>
                }
              >
                <HvButton
                  icon
                  category="ghost"
                  aria-label={t("steps.columns.actions.drag")}
                  {...{ component: "div" }}
                  {...dragHandleProps}
                >
                  <Drag />
                </HvButton>
              </HvTooltip>
            </div>
          );
        },
      }),
    ],
    [t, classes]
  );

  const [, setScrollTo] = useScrollTo(
    0,
    "wis-container",
    false,
    100,
    [
      {
        label: t("steps.title"),
        value: "steps",
      },
      {
        value: "topButton",
      },
    ],
    undefined
  );

  const handleAddEditStep = (
    stepData: SopStep,
    addAnother: boolean | undefined
  ) => {
    if (
      steps.findIndex((step) => step.name === stepData.name) > -1 &&
      mode === "add"
    ) {
      throw new Error("name_already_exists");
    }

    if (mode === "add") {
      const step = { ...stepData, id: uniqueId() };
      push(step);
      setScrollTo(null, "topButton", 0, undefined);
      setIsDialogOpen(addAnother || false);

      return {
        ...step,
        order: steps.length,
      };
    }

    replace(
      steps.findIndex((s) => s.id === stepData.id),
      stepData
    );
    setIsDialogOpen(addAnother || false);
    return undefined;
  };

  const handleDelete = (selectedRowIds: Record<string, boolean>) => {
    const selectedRowOrders = Object.keys(selectedRowIds);
    let numberDeleted = 0;

    steps.forEach((step, index) => {
      if (selectedRowOrders.includes(step.id as string)) {
        // remove updates the indexes inside formik, so we need to update indexes while we remove
        remove(index - numberDeleted);
        numberDeleted += 1;
      }
    });
  };

  const onDragEndHandler = (result: DropResult) => {
    move(result.source.index, result.destination?.index as number);
  };

  return (
    <HvGrid container spacing={2}>
      <HvGrid sm={12} item>
        <HvGlobalActions title={t("steps.title")} variant="section" />
        <HvTypography variant="normalText">
          {t("steps.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid sm={12} item>
        <HvGrid container justifyContent="flex-end">
          <HvGrid item>
            <HvButton
              category="semantic"
              startIcon={<Add />}
              onClick={() => openDialog()}
            >
              {t("buttons.add")}
            </HvButton>
          </HvGrid>
        </HvGrid>

        <StepsDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleAddEditStep}
          initialValues={dialogValues}
          mode={mode}
        />
      </HvGrid>
      <HvGrid sm={12} item>
        <DragDropList
          columns={columns}
          data={steps}
          actions={[
            { id: "delete", label: t("buttons.delete"), icon: <Delete /> },
          ]}
          actionsCallback={handleDelete}
          emptyState={
            <HvEmptyState
              title={t("steps.emptyState.title")}
              message={t("steps.emptyState.description")}
              icon={<Info />}
            />
          }
          onDragEnd={onDragEndHandler}
        />

        {steps.length >= 10 && (
          <HvGrid container justifyContent="flex-end">
            <HvGrid item>
              <HvTooltip title={<HvTypography>Go to Top</HvTypography>}>
                <HvButton
                  id="topButton"
                  category="semantic"
                  aria-label={t("steps.buttons.top.description")}
                  startIcon={<Top />}
                  onClick={(event) => setScrollTo(event, "steps", 0, undefined)}
                >
                  {t("steps.buttons.top.label")}
                </HvButton>
              </HvTooltip>
            </HvGrid>
          </HvGrid>
        )}
      </HvGrid>
    </HvGrid>
  );
};

export default SopAddEditSteps;
